import { useCallback, useEffect, useState } from "react";
import { Link } from "wouter";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  COOKIE_CONSENT_OPEN_EVENT,
  hasConsentChoice,
  saveConsentChoice,
} from "@/lib/cookie-consent";

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  const refreshVisibility = useCallback(() => {
    setVisible(!hasConsentChoice());
  }, []);

  useEffect(() => {
    refreshVisibility();
  }, [refreshVisibility]);

  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener(COOKIE_CONSENT_OPEN_EVENT, open);
    return () => window.removeEventListener(COOKIE_CONSENT_OPEN_EVENT, open);
  }, []);

  const choose = async (choice: "all" | "essential") => {
    setSaving(true);
    try {
      await saveConsentChoice(choice);
      setVisible(false);
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
      className={cn(
        "fixed inset-x-0 bottom-0 z-[100] border-t border-border/60 bg-background/95 p-4 shadow-lg backdrop-blur-md",
        "sm:p-5",
      )}
    >
      <div className="container mx-auto flex max-w-5xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-3 sm:max-w-2xl">
          <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-[#0071BC]" aria-hidden />
          <div>
            <p id="cookie-consent-title" className="font-semibold text-foreground">
              ملفات تعريف الارتباط (الكوكيز)
            </p>
            <p id="cookie-consent-desc" className="mt-1 text-sm leading-relaxed text-muted-foreground">
              نستخدم ملفات تعريف الارتباط الضرورية لتأمين جلستك عند تسجيل الدخول، وملفات
              اختيارية لتحسين تجربتك.{" "}
              <Link href="/cookies" className="text-[#0071BC] underline-offset-2 hover:underline">
                سياسة الكوكيز
              </Link>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={saving}
            onClick={() => choose("essential")}
          >
            رفض غير الضرورية
          </Button>
          <Button
            type="button"
            size="sm"
            className="bg-[#0071BC] hover:bg-[#005a94]"
            disabled={saving}
            onClick={() => choose("all")}
          >
            قبول الكل
          </Button>
        </div>
      </div>
    </div>
  );
}
